// Shared display-only formatters used across Nivo CRM.
// IMPORTANT: these transform values for the UI ONLY. The database is never
// modified. Keep this file free of side-effects.

/**
 * Title Case for client names — display-only.
 * Rules:
 *   - Lowercase the whole string first.
 *   - Uppercase the first Unicode letter of every word segment separated by
 *     whitespace, hyphen ("-") or apostrophe ("'" / "’").
 *   - Preserve accented characters ("é", "à", "ç" …).
 *
 * Examples:
 *   "issam"           → "Issam"
 *   "BRAHMI HAMZA"    → "Brahmi Hamza"
 *   "jean-pierre"     → "Jean-Pierre"
 *   "d'ali al-hassan" → "D'Ali Al-Hassan"
 *   "  jean   "       → "  Jean   " (whitespace preserved)
 */
export function formatClientName(name) {
    if (name === null || name === undefined) return name;
    if (typeof name !== 'string') return name;
    if (name.length === 0) return name;
    const lower = name.toLocaleLowerCase('fr-FR');
    // \p{L} matches any Unicode letter. The "u" flag enables Unicode escapes.
    // Boundary: start-of-string OR whitespace / hyphen / apostrophe / curly apostrophe.
    return lower.replace(
        /(^|[\s\-'’])(\p{L})/gu,
        (_, sep, ch) => sep + ch.toLocaleUpperCase('fr-FR'),
    );
}

/**
 * French elapsed time — display-only.
 * Contract (Phase 13.9):
 *   seconds < 60     → "à l'instant"
 *   minutes < 60     → "il y a Xmin"
 *   hours   < 24     → "il y a Xh Ymin"   (Y zero-padded to 2 digits when > 0)
 *                    → "il y a Xh"        (when Y === 0)
 *   otherwise        → "il y a Xj Yh"     (when Y > 0)
 *                    → "il y a Xj"        (when Y === 0)
 */
export function frElapsed(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < 60) return "à l'instant";
    const mins = Math.floor(s / 60);
    if (mins < 60) return `il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    const remMin = mins - hours * 60;
    if (hours < 24) {
        return remMin > 0
            ? `il y a ${hours}h ${remMin.toString().padStart(2, '0')}min`
            : `il y a ${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remH = hours - days * 24;
    return remH > 0 ? `il y a ${days}j ${remH}h` : `il y a ${days}j`;
}

/**
 * Short elapsed for a "last updated" style counter:
 *   seconds < 60 → "il y a Xs"
 *   otherwise    → delegate to frElapsed().
 */
export function frElapsedShort(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < 60) return `il y a ${s}s`;
    return frElapsed(s);
}
